import { TFunction } from 'i18next';

const SALARY_DECIMAL_REGEX = /^\d+(\.\d+)?$/;
type SalaryFieldName = 'salaryFrom' | 'salaryTo';
type SalaryFieldValue = string | number | null | undefined;
type GetFieldValue = (name: string) => unknown;

export const normalizeSalaryInput = (
  value: SalaryFieldValue
): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value).trim().replace(',', '.');
};

export const parseSalaryNumber = (value: SalaryFieldValue): number | null => {
  const normalized = normalizeSalaryInput(value);

  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};

export const hasSalaryValue = (
  salaryFrom: SalaryFieldValue,
  salaryTo: SalaryFieldValue
): boolean =>
  parseSalaryNumber(salaryFrom) !== null ||
  parseSalaryNumber(salaryTo) !== null;

const createSalaryValueValidator =
  (t: TFunction) =>
  ({ getFieldValue }: { getFieldValue: GetFieldValue }) => ({
    validator(_: unknown, value: SalaryFieldValue) {
      if (getFieldValue('salaryNotSpecified')) {
        return Promise.resolve();
      }

      if (value === null || value === undefined || value === '') {
        return Promise.resolve();
      }

      const normalized = normalizeSalaryInput(value);

      if (!normalized || !SALARY_DECIMAL_REGEX.test(normalized)) {
        return Promise.reject(new Error(t('form.salary.validation.invalid')));
      }

      const parsed = Number(normalized);

      if (parsed < 1) {
        return Promise.reject(new Error(t('form.salary.validation.min')));
      }

      return Promise.resolve();
    }
  });

const getSalaryFieldValue = (
  getFieldValue: GetFieldValue,
  fieldName: string
): SalaryFieldValue => getFieldValue(fieldName) as SalaryFieldValue;

const getSalaryValues = (
  getFieldValue: GetFieldValue,
  fieldName: SalaryFieldName,
  value: SalaryFieldValue
) => ({
  salaryFrom:
    fieldName === 'salaryFrom'
      ? parseSalaryNumber(value)
      : parseSalaryNumber(getSalaryFieldValue(getFieldValue, 'salaryFrom')),
  salaryTo:
    fieldName === 'salaryTo'
      ? parseSalaryNumber(value)
      : parseSalaryNumber(getSalaryFieldValue(getFieldValue, 'salaryTo'))
});

const createSalaryRangeValidator =
  (t: TFunction, fieldName: SalaryFieldName) =>
  ({ getFieldValue }: { getFieldValue: GetFieldValue }) => ({
    validator(_: unknown, value: SalaryFieldValue) {
      if (getFieldValue('salaryNotSpecified')) {
        return Promise.resolve();
      }

      const { salaryFrom, salaryTo } = getSalaryValues(
        getFieldValue,
        fieldName,
        value
      );

      if (salaryFrom !== null && salaryTo !== null && salaryFrom > salaryTo) {
        return Promise.reject(new Error(t('form.salary.validation.range')));
      }

      return Promise.resolve();
    }
  });

const createSalaryAtLeastOneValidator =
  (t: TFunction, fieldName: SalaryFieldName) =>
  ({ getFieldValue }: { getFieldValue: GetFieldValue }) => ({
    validator(_: unknown, value: SalaryFieldValue) {
      if (getFieldValue('salaryNotSpecified')) {
        return Promise.resolve();
      }

      const { salaryFrom, salaryTo } = getSalaryValues(
        getFieldValue,
        fieldName,
        value
      );

      if (salaryFrom === null && salaryTo === null) {
        return Promise.reject(
          new Error(t('form.salary.validation.atLeastOne'))
        );
      }

      return Promise.resolve();
    }
  });

const createCurrencyRequiredValidator =
  (t: TFunction) =>
  ({ getFieldValue }: { getFieldValue: GetFieldValue }) => ({
    validator(_: unknown, value: string | null | undefined) {
      if (getFieldValue('salaryNotSpecified')) {
        return Promise.resolve();
      }

      if (
        hasSalaryValue(
          getSalaryFieldValue(getFieldValue, 'salaryFrom'),
          getSalaryFieldValue(getFieldValue, 'salaryTo')
        ) &&
        !value
      ) {
        return Promise.reject(new Error(t('form.currency.validation')));
      }

      return Promise.resolve();
    }
  });

const createPayrollRequiredValidator =
  (t: TFunction) =>
  ({ getFieldValue }: { getFieldValue: GetFieldValue }) => ({
    validator(_: unknown, value: string | null | undefined) {
      if (getFieldValue('salaryNotSpecified')) {
        return Promise.resolve();
      }

      if (
        hasSalaryValue(
          getSalaryFieldValue(getFieldValue, 'salaryFrom'),
          getSalaryFieldValue(getFieldValue, 'salaryTo')
        ) &&
        !value
      ) {
        return Promise.reject(new Error(t('form.payroll.validation')));
      }

      return Promise.resolve();
    }
  });

export {
  createCurrencyRequiredValidator,
  createPayrollRequiredValidator,
  createSalaryAtLeastOneValidator,
  createSalaryRangeValidator,
  createSalaryValueValidator
};
